import { ISalesforceSchema } from '@monetize/types/app';
import isUndefined from 'lodash/isUndefined';
import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  doGetCrmAuthorizeRedirectUrl,
  useGetCrmConfiguration,
} from '../../../../api/settingsService';
import {
  MAlert,
  MBox,
  MFlex,
  MPageContainer,
  MSettingsPageHeader,
  MSkeleton,
  MText,
} from '../../../../components/Monetize';
import { SettingCardEdit } from '../../../../components/Settings/SettingCardEdit';
import { SettingCardEmpty } from '../../../../components/Settings/SettingCardEmpty';
import { useDocumentHead } from '../../../../services/documentHead';
import { useToast } from '../../../../services/toast';
import { appGlobalDataState } from '../../../../store/global.store';
import { AttioCrmConnection } from './components/attio/AttioCrmConnection';
import { HubspotCrmConnection } from './components/hubspot/HubspotCrmConnection';
import { SalesforceCrmConnection } from './components/salesforce/SalesforceCrmConnection';
import { SalesforceForm } from './components/salesforce/SalesforceForm';
import { useCrmConnectionHealth } from './useCrmConnectionHealth';

const CrmConnections = () => {
  const [newConnectionType, setNewConnectionType] = useState<
    'HUBSPOT' | 'SALESFORCE' | 'ATTIO' | null
  >(null);

  const { hasCrmConfigured } = useAtomValue(appGlobalDataState);

  const { data: crmConnections, isLoading } = useGetCrmConfiguration({
    enabled: hasCrmConfigured, // already fetched and checked in global store if we have crm configured
    meta: { showErrorToast: true },
  });
  const { addToast } = useToast();

  const hubspotConnection = crmConnections?.hubspot;
  const salesforceConnection = crmConnections?.salesforce;
  const attioConnection = crmConnections?.attio;

  const { health, recheck } = useCrmConnectionHealth({
    enabled: !!(hubspotConnection || salesforceConnection || attioConnection),
  });

  const { setDocTitle } = useDocumentHead();
  useEffect(() => {
    setDocTitle('Settings', 'CRM');
  }, []);

  const initiateSalesforceOAuth = async (data: ISalesforceSchema) => {
    try {
      const { url } = await doGetCrmAuthorizeRedirectUrl(
        'salesforce',
        data.customUrl,
      );
      window.location.href = url;
    } catch (ex) {
      handleApiErrorToast(ex);
    }
  };

  const initiateHubSpotOAuth = async () => {
    try {
      const { url } = await doGetCrmAuthorizeRedirectUrl('hubspot');
      window.location.href = url;
    } catch (ex) {
      handleApiErrorToast(ex);
    }
  };

  const initiateAttioOAuth = async () => {
    try {
      const { url } = await doGetCrmAuthorizeRedirectUrl('attio');
      window.location.href = url;
    } catch (ex) {
      handleApiErrorToast(ex);
    }
  };

  const verifyConnection = async (
    connectionTypeText: 'Salesforce' | 'Hubspot' | 'Attio',
  ) => {
    const { status } = await recheck();
    if (status === 'healthy') {
      addToast({
        summary: 'Connection Verified',
        detail: `Your ${connectionTypeText} connection is active, verify that it is connected in the ${connectionTypeText} App.`,
        severity: 'success',
      });
    } else {
      addToast({
        summary: 'Connection Verification Failed',
        detail: `There was an issue connecting to ${connectionTypeText}, check your connection setup or contact support for the help resolving this.`,
        severity: 'error',
      });
    }
  };

  return (
    <MPageContainer alignItems="stretch">
      <MSettingsPageHeader title="Customer Relationship Management (CRM)" />

      <MBox maxW="600px">
        <MText color="tGray.darkPurple" mb={4}>
          Connect your CRM to quote, manage reporting, and sync data between
          MonetizeNow and your CRM.
        </MText>

        {isUndefined(crmConnections) && !hasCrmConfigured && !isLoading && (
          <MAlert type="error" mb={8}>
            <MText>
              Unable to retrieve CRM configuration. Please ensure you have
              configured your CRM settings correctly or contact support for
              assistance. Creating a new connection will overwrite the existing
              configuration.
            </MText>
          </MAlert>
        )}

        {isLoading ? (
          <MSkeleton height={36} w="full" />
        ) : (
          <MFlex>
            {/* NO CONNECTION */}
            {!salesforceConnection &&
              !hubspotConnection &&
              !attioConnection &&
              !newConnectionType && (
                <MFlex gap={6}>
                  <SettingCardEmpty
                    type="Salesforce"
                    onClick={() => setNewConnectionType('SALESFORCE')}
                  />
                  <SettingCardEmpty
                    type="HubSpot"
                    onClick={initiateHubSpotOAuth}
                  />
                  <SettingCardEmpty type="Attio" onClick={initiateAttioOAuth} />
                </MFlex>
              )}

            {/* NEW SFDC CONNECTION */}
            {newConnectionType === 'SALESFORCE' && (
              <SettingCardEdit type="Salesforce">
                <SalesforceForm
                  onAuthorize={initiateSalesforceOAuth}
                  handleCancel={() => setNewConnectionType(null)}
                />
              </SettingCardEdit>
            )}

            {/* HAS CONNECTION CONFIGURED */}
            {salesforceConnection && (
              <MFlex flexDirection="column" gap={6}>
                <SalesforceCrmConnection
                  health={health}
                  connection={salesforceConnection}
                  onAuthorize={initiateSalesforceOAuth}
                  verifyConnection={() => verifyConnection('Salesforce')}
                />
              </MFlex>
            )}
            {hubspotConnection && (
              <MFlex flexDirection="column" gap={6}>
                <HubspotCrmConnection
                  health={health}
                  connection={hubspotConnection}
                  reAuthorize={initiateHubSpotOAuth}
                  verifyConnection={() => verifyConnection('Hubspot')}
                />
              </MFlex>
            )}
            {attioConnection && (
              <MFlex flexDirection="column" gap={6}>
                <AttioCrmConnection
                  health={health}
                  connection={attioConnection}
                  reAuthorize={initiateAttioOAuth}
                  verifyConnection={() => verifyConnection('Attio')}
                />
              </MFlex>
            )}
          </MFlex>
        )}
      </MBox>
    </MPageContainer>
  );
};

export default CrmConnections;
